import { isMatrixDrag, readDroppedMatrixId } from '@/lib/matrix-drag';
import { cn } from '@/lib/utils';
import { Icon } from '@pms-digital/design-system';
import { type ReactNode, useState } from 'react';

export interface FolderSidebarItemProps {
  icon: ReactNode;
  label: string;
  /** The row is the current view. */
  selected?: boolean;
  /** Matrices in it. */
  count?: number;
  /** Open notes of a folder; the gray bubble shows nothing at zero. */
  noteCount?: number;
  onSelect: () => void;
  /** Makes the row a drop target for a dragged matrix. */
  onDropMatrix?: (matrixId: string) => void;
  /** Tints the label, for « Nouveau dossier ». */
  accent?: boolean;
}

/**
 * One row of the folders column (planche 14): icon, name, the gray note
 * bubble and the matrix count. When `onDropMatrix` is given, a matrix dragged
 * over it lights the row up and a drop hands the matrix id back (planche 15).
 */
export function FolderSidebarItem({
  icon,
  label,
  selected = false,
  count,
  noteCount = 0,
  onSelect,
  onDropMatrix,
  accent = false,
}: FolderSidebarItemProps) {
  const [dropTarget, setDropTarget] = useState(false);

  const dropProps = onDropMatrix
    ? {
        onDragOver: (event: React.DragEvent<HTMLButtonElement>) => {
          if (!isMatrixDrag(event)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          setDropTarget(true);
        },
        onDragLeave: () => setDropTarget(false),
        onDrop: (event: React.DragEvent<HTMLButtonElement>) => {
          setDropTarget(false);
          const matrixId = readDroppedMatrixId(event);
          if (matrixId === null) return;
          event.preventDefault();
          onDropMatrix(matrixId);
        },
      }
    : {};

  return (
    <button
      type="button"
      aria-current={selected ? 'page' : undefined}
      data-drop-target={dropTarget || undefined}
      onClick={onSelect}
      {...dropProps}
      className={cn(
        'flex w-full items-center gap-2 rounded-md border-2 border-transparent px-2 py-1 text-left text-caption focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit',
        selected
          ? 'bg-bleu-nuit-tint font-semibold text-bleu-nuit'
          : accent
            ? 'text-bleu-nuit hover:bg-gray-50'
            : 'text-gray-800 hover:bg-gray-50',
        dropTarget && 'border-bleu-nuit border-dashed bg-bleu-nuit-tint',
      )}
    >
      <span
        aria-hidden="true"
        className={cn('shrink-0 [&_svg]:size-4', selected ? 'text-bleu-nuit' : 'text-gray-500')}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1 break-words">{label}</span>
      {noteCount > 0 && (
        <span
          title={noteCount === 1 ? '1 note ouverte' : `${noteCount} notes ouvertes`}
          className="inline-flex shrink-0 items-center gap-0 font-normal text-caption text-gray-500"
        >
          <Icon name="chat_bubble" size={14} />
          <span className="sr-only">Notes ouvertes : </span>
          {noteCount}
        </span>
      )}
      {count !== undefined && (
        <span className="w-4 shrink-0 text-right font-normal text-caption text-gray-500">
          {count}
        </span>
      )}
    </button>
  );
}
